import dayjs from 'dayjs'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '@/store'
import { API_BASE_URL } from '@/config/api'
import { getStoredToken } from '@/services/auth'
import ProcurementPurchaseListView from '@/pages/procurement/ProcurementPurchaseListView'
import ProcurementContractDetailView from '@/pages/procurement/ProcurementContractDetailView'
import ProcurementPayDataListView from '@/pages/procurement/ProcurementPayDataListView'
import ProcurementInvoiceListView from '@/pages/procurement/ProcurementInvoiceListView'
import ProcurementGoodAllocationListView from '@/pages/procurement/ProcurementGoodAllocationListView'
import ProcurementReferencesView from '@/components/references/InboundReferencesView'
import { REFERENCE_CATALOG, type ReferencePageKey } from '@/components/references/catalog'
import ProcurementCVBBListView from '@/pages/procurement/ProcurementCVBBListView'
import ProcurementQualityPriceDialog from '@/pages/procurement/ProcurementQualityPriceDialog'
import ComingSoonPage from '@/pages/common/ComingSoonPage'
import { getUnbuild } from '@/config/pageBuildStatus'
import PageHeader from '@/components/PageHeader'
import ReloadButton from '@/components/ReloadButton'
import {
  CARD_CONTENT_COMPACT,
  FILTER_HEADER_INPUT_SX,
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_TABLET_INLINE,
} from '@/theme/responsive'
import { usePageFilters } from '@/hooks/usePageFilters'
import { usePreference } from '@/hooks/usePreference'

export type ForcedCloseFilter = 'all' | 'yes' | 'no'

export interface ProcurementFilters extends Record<string, unknown> {
  startDate: string
  endDate: string
  isForcedClose: ForcedCloseFilter
}

type ProcurementTabKey =
  | 'purchase-contract'
  | 'contract-detail'
  | 'pay-data'
  | 'invoice'
  | 'goods-allocation'
  | 'references'
  | 'cvbb'

interface ProcurementTabConfig {
  key: ProcurementTabKey
  label: string
  accessLabel: string
}

const FILTER_PAGE_KEY = 'procurement'

const TAB_CONFIG: ProcurementTabConfig[] = [
  { key: 'purchase-contract', label: 'Purchase', accessLabel: 'Purchase' },
  { key: 'contract-detail', label: 'Contract Detail', accessLabel: 'Contract Detail' },
  { key: 'pay-data', label: 'Pay Data', accessLabel: 'Pay Data' },
  { key: 'invoice', label: 'Invoice', accessLabel: 'Invoice' },
  { key: 'goods-allocation', label: 'Goods Allocation', accessLabel: 'Goods Allocation' },
  { key: 'cvbb', label: 'Documents & Minutes', accessLabel: 'Documents & Minutes' },
  { key: 'references', label: 'References', accessLabel: 'References' },
]

const DEFAULT_TAB_KEY: ProcurementTabKey = 'purchase-contract'

function isProcurementTabKey(value: string | undefined): value is ProcurementTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

function getDefaultFilters(): ProcurementFilters {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
    isForcedClose: 'all',
  }
}

function normalizeStoredFilters(raw: unknown): ProcurementFilters {
  const defaults = getDefaultFilters()
  if (!raw || typeof raw !== 'object') return defaults
  const parsed = raw as Partial<ProcurementFilters>
  return {
    startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : defaults.startDate,
    endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : defaults.endDate,
    isForcedClose:
      parsed.isForcedClose === 'yes' || parsed.isForcedClose === 'no' || parsed.isForcedClose === 'all'
        ? parsed.isForcedClose
        : 'all',
  }
}

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

// Filter chung header sx — codify trong `@/theme/responsive` Pattern 11.
const compactInputSx = FILTER_HEADER_INPUT_SX
const compactSelectSx = FILTER_HEADER_SELECT_SX

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function ProcurementPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { filters: storedFilters, setFilters: persistFilters } = usePageFilters<ProcurementFilters>(FILTER_PAGE_KEY)
  const filters = useMemo(() => normalizeStoredFilters(storedFilters), [storedFilters])
  const setFilters = persistFilters as (next: ProcurementFilters | ((prev: ProcurementFilters | undefined) => ProcurementFilters)) => void
  const navPref = usePreference<{ lastTab: string }>('procurement', 'navigation', undefined, { refetchOnMount: true })
  const [qualityPriceOpen, setQualityPriceOpen] = useState(false)
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const email = useAppSelector((s) => s.auth.user?.email ?? '')

  const downloadProcurementExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getStoredToken() ?? ''}` },
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'Procurement_filter.xlsx' : 'Procurement.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/procurement/data`
    await downloadProcurementExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/procurement/data_filter_2?start_date=${encodeURIComponent(filters.startDate)}&end_date=${encodeURIComponent(filters.endDate)}&is_forced_close=${encodeURIComponent(filters.isForcedClose)}`
    await downloadProcurementExcel('filter', url)
  }

  const activeKey: ProcurementTabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    return isProcurementTabKey(candidate) ? candidate : DEFAULT_TAB_KEY
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/purchase/control')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    if (isProcurementTabKey(candidate)) return
    // Không có sub-tab hợp lệ trên URL → restore tab cuối user đã mở
    // (nếu navPref đã fetch xong). Fallback DEFAULT_TAB_KEY nếu chưa lưu.
    if (navPref.isLoading || navPref.isFetching) return
    const saved = navPref.value?.lastTab
    const target = saved && isProcurementTabKey(saved) ? saved : DEFAULT_TAB_KEY
    navigate(`/purchase/control/${target}`, { replace: true })
  }, [location.pathname, navigate, navPref.isLoading, navPref.isFetching, navPref.value])

  // Auto-save khi user đổi tab: lưu tab hiện tại. Debounce 400ms để click
  // nhanh không spam BE.
  useEffect(() => {
    if (navPref.isLoading || navPref.isFetching) return
    // CHỈ lưu tab THẬT đang có trên URL (segment thô). Ở route gốc segment rỗng →
    // KHÔNG lưu (tránh ghi đè bằng tab default lúc chưa restore xong).
    const urlTab = location.pathname.split('/').filter(Boolean)[2]
    if (!isProcurementTabKey(urlTab)) return
    if (navPref.value?.lastTab === urlTab) return
    void navPref.patch({ lastTab: urlTab }).catch(() => undefined)
  }, [location.pathname, navPref.value?.lastTab, navPref.patch])

  const activeTab = TAB_CONFIG.find((tab) => tab.key === activeKey) ?? TAB_CONFIG[0]

  // References tab: chỉ 2 page dùng shared "Inbound Logistics" filter
  // (ContractStatistics/PendingPayment). Lift pageKey lên đây để enable/disable
  // Date + Is Forced Close ở header theo page đang chọn (đồng bộ Outbound).
  const gotoPref = usePreference<{ lastPage: string }>('procurement', 'goto_page', undefined, { refetchOnMount: true })
  const refPageKey = ((): ReferencePageKey => {
    const v = gotoPref.value?.lastPage
    return v && v in REFERENCE_CATALOG ? (v as ReferencePageKey) : 'contract-statistics'
  })()
  const setRefPageKey = (k: ReferencePageKey) => {
    void gotoPref.patch({ lastPage: k }).catch(() => undefined)
  }
  const isRefTab = activeTab.key === 'references'
  // Filter do page tự khai (catalog) → page không khai filter nào thì disable header.
  const disableRefFilter = isRefTab && !(REFERENCE_CATALOG[refPageKey].filters?.length)

  // Endpoint names theo tab — nút Reload "busy" khi 1 trong các query này đang pending.
  // Các tab pay-data/invoice/goods-allocation/references/cvbb hiện là placeholder, không fetch.
  const watchedEndpoints = useMemo<string[]>(() => {
    switch (activeKey) {
      case 'purchase-contract':
        return ['listLocalP']
      case 'goods-allocation':
        return ['listGoodAllocations']
      case 'pay-data':
        return ['listPayData']
      case 'invoice':
        return ['listInvoices']
      case 'cvbb':
        return ['listCvbb']
      case 'references':
        return ['getProcurementReferenceRows']
      case 'contract-detail':
        return [
          'getContractPicks',
          'getContractHeaderP',
          'getContractHeaderNPE',
          'listContractGoodsAlc',
          'listContractPaydata',
          'listContractInvoice',
          'listContractAdvance',
          'listContractFixation',
        ]
      default:
        return []
    }
  }, [activeKey])
  const hasInvalidRange = filters.startDate > filters.endDate

  // Resolve-first: URL chưa có sub-tab hợp lệ (đang đọc DB + chờ restore) → hiện
  // loading, KHÔNG render Page default. Có Page đúng trên URL mới render.
  if (!isProcurementTabKey(location.pathname.split('/').filter(Boolean)[2]))
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Purchase Control"
        module="Purchase"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          {/* Date RANGE filter (Pattern 11) — style copy 1:1 từ Purchase Contract:
              DatePicker (MUI-X) đầu có label, icon → ở giữa, DatePicker thứ 2 không label. */}
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Delivery Lastest"
                format="DD-MMM-YYYY"
                disabled={disableRefFilter}
                value={filters.startDate ? dayjs(filters.startDate) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...getDefaultFilters(),
                    ...prev,
                    startDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().startDate,
                  }))
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: compactInputSx,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': 'Delivery Lastest start date' },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{ color: disableRefFilter ? 'text.disabled' : 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                disabled={disableRefFilter}
                value={filters.endDate ? dayjs(filters.endDate) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...getDefaultFilters(),
                    ...prev,
                    endDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().endDate,
                  }))
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !disableRefFilter && hasInvalidRange,
                    sx: compactInputSx,
                    inputProps: { 'aria-label': 'Delivery Lastest end date' },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          {/* Is Forced Close — Pattern 11: label inside (shrink). Style copy 1:1
              từ Purchase Contract: TextField select (MenuItem All/Yes/No). */}
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField
              label="Is Forced Close"
              select
              size="small"
              value={filters.isForcedClose}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...getDefaultFilters(),
                  ...prev,
                  isForcedClose: event.target.value as ForcedCloseFilter,
                }))
              }
              disabled={disableRefFilter}
              InputLabelProps={{ shrink: true }}
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={() => setQualityPriceOpen(true)}
            title="Quality Price (vs Basis)"
          >
            {/* sm: "Quality" — md+: "Quality Price (vs Basis)" */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Quality Price (vs Basis)</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Quality</Box>
          </Button>
          <ReloadButton
            sx={headerButtonSx}
            apiTags={[]}
            baseTags={[
              // Purchase
              'ContractAll', 'Refs', 'Annex', 'CSFixation', 'LocalP', 'LocalPPlan',
              // Pay Data / Invoice / Goods Allocation / CVBB list tags
              'PayData', 'ProcInvoice', 'ProcGoodAlc', 'ProcCvbb',
              // Contract Detail datasheets (getContractPicks tự refetch qua ContractAll)
              'CDGoodsAlc', 'CDInvoice', 'CDPaydata', 'CDAdvance', 'CDFixation',
              // References switchboard (16 trang read-only)
              'ProcReference',
            ]}
            watchEndpoints={watchedEndpoints}
          />
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportFilter}
            disabled={exporting !== null || !email || hasInvalidRange}
            startIcon={
              exporting === 'filter' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (Filter)"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            Filter
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportAll}
            disabled={exporting !== null || !email}
            startIcon={
              exporting === 'all' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (All)"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            All
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
        </Stack>
        }
      />

      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: ProcurementTabKey) => {
            void navPref.patch({ lastTab: value }).catch(() => undefined)
            navigate(`/purchase/control/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={CARD_CONTENT_COMPACT}>
          {/*
            All Purchase Control (Procurement) tabs are parked behind ComingSoonPage until rebuild lands.
            View components (ProcurementPurchaseListView, ProcurementContractDetailView,
            ProcurementPayDataListView, ProcurementInvoiceListView, ProcurementGoodAllocationListView,
            ProcurementReferencesView, ProcurementCVBBListView) remain imported so restore is just
            swapping the body below back to the original conditional.

            Original body (pre-park, restore as-is when rebuilding):
              {activeTab.key === 'purchase-contract' ? (
                <ProcurementPurchaseListView filters={filters} />
              ) : activeTab.key === 'contract-detail' ? (
                <ProcurementContractDetailView filters={filters} />
              ) : activeTab.key === 'pay-data' ? (
                <ProcurementPayDataListView filters={filters} />
              ) : activeTab.key === 'invoice' ? (
                <ProcurementInvoiceListView filters={filters} />
              ) : activeTab.key === 'goods-allocation' ? (
                <ProcurementGoodAllocationListView filters={filters} />
              ) : activeTab.key === 'references' ? (
                <ProcurementReferencesView filters={filters} pageKey={refPageKey} onPageKeyChange={setRefPageKey} />
              ) : activeTab.key === 'cvbb' ? (
                <ProcurementCVBBListView filters={filters} />
              ) : (
                <Box sx={{ py: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>{activeTab.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This screen has not been migrated from Access yet. Please use the legacy Access screen for full
                    workflows until this tab is implemented.
                  </Typography>
                </Box>
              )}
          */}
          {getUnbuild(`/purchase/control/${activeTab.key}`) ? (
            <ComingSoonPage
              title={activeTab.label}
              embedded
              status={getUnbuild(`/purchase/control/${activeTab.key}`)?.status}
            />
          ) : activeTab.key === 'purchase-contract' ? (
            <ProcurementPurchaseListView filters={filters} />
          ) : activeTab.key === 'contract-detail' ? (
            <ProcurementContractDetailView />
          ) : activeTab.key === 'pay-data' ? (
            <ProcurementPayDataListView filters={filters} />
          ) : activeTab.key === 'invoice' ? (
            <ProcurementInvoiceListView filters={filters} />
          ) : activeTab.key === 'goods-allocation' ? (
            <ProcurementGoodAllocationListView filters={filters} />
          ) : activeTab.key === 'references' ? (
            <ProcurementReferencesView filters={filters} pageKey={refPageKey} onPageKeyChange={setRefPageKey} />
          ) : activeTab.key === 'cvbb' ? (
            <ProcurementCVBBListView filters={filters} />
          ) : (
            <ComingSoonPage
              title={activeTab.label}
              embedded
              status={getUnbuild(`/purchase/control/${activeTab.key}`)?.status}
            />
          )}
        </CardContent>
      </Card>

      <ProcurementQualityPriceDialog open={qualityPriceOpen} onClose={() => setQualityPriceOpen(false)} />
    </Box>
  )
}
